/**
 * The recommendation surfaces' list logic (recommender plan, section 6),
 * kept pure so the unit tests read it:
 *
 *  - usableFeed: whether an answer of GET /api/rec/for-you can stand in
 *    for the surface's own list, or the surface keeps its current source
 *    (no index yet, the popularity fallback, the wrong side of the market);
 *  - the hide flow: a card leaves the list at once and comes back to the
 *    same place on undo, keeping the position it was shown at;
 *  - recHref: a card's link carrying `via`, `rec` and `pos`.
 */
import type { ListingCard } from "@/types";

/** The engine also sends machine-readable `reasons` per card; nothing
 *  renders them (owner, 2026-11-03: the chips were not useful). */
export type RecCard = ListingCard;

export interface RecAnswer {
  items: RecCard[];
  rec_request_id: string;
  algo: string;
}

/** A recommended card with the position it was shown at (0-based). */
export interface Placed {
  card: RecCard;
  pos: number;
}

/** Algorithms that know nothing about the person or the page: the
 *  surface's own list says more than these. */
const NOT_PERSONAL = new Set(["none", "popular-v1"]);

/** The cards to show from a for-you answer, or null for "keep the
 *  current source". `leased` keeps a buyer's row free of rentals and the
 *  other way round (the engine's segment follows the history, the
 *  surface's goal may have moved since). Fewer than `min` cards left is
 *  not a row. */
export function usableFeed(
  ans: RecAnswer | null | undefined,
  { leased = null, min = 3, max = 16 }: { leased?: boolean | null; min?: number; max?: number } = {},
): Placed[] | null {
  if (!ans || !Array.isArray(ans.items) || !ans.rec_request_id || NOT_PERSONAL.has(ans.algo)) return null;
  // Positions count the cards shown, so a card's `pos` is the one the
  // impression log writes for it.
  const out: Placed[] = [];
  for (const card of ans.items) {
    if (out.length >= max) break;
    if (!card || !card.id) continue;
    if (leased != null && card.leased != null && card.leased !== leased) continue;
    out.push({ card, pos: out.length });
  }
  return out.length >= min ? out : null;
}

/** The list without one card (the "not for me" tap). */
export function withoutCard(list: Placed[], id: string): Placed[] {
  return list.filter((p) => p.card.id !== id);
}

/** The list with a card back where it was (undo): ordered by the
 *  position it was shown at, never twice. */
export function withCard(list: Placed[], item: Placed): Placed[] {
  if (list.some((p) => p.card.id === item.card.id)) return list;
  return [...list, item].sort((a, b) => a.pos - b.pos);
}

/** The ids of a shown list in the order shown (the impression log). */
export function shownIds(list: Placed[], count = list.length): string[] {
  return list.slice(0, count).map((p) => p.card.id);
}

/** A card's link with its attribution: `?via=<surface>&rec=<id>&pos=<n>`
 *  (lib/entry-context reads them on the landing). */
export function recHref(path: string, via: string, rec: string, pos: number): string {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}via=${encodeURIComponent(via)}&rec=${encodeURIComponent(rec)}&pos=${pos}`;
}
